import AppForm from '@/components/AppForm';
import { ErrorResponseModal, SuccessResponseModal } from '@/components/AppModal/modals';
import AppPageTitle from '@/components/AppPageTitle';
import PATH from '@/config/paths/PATH';
import useFormSubmit from '@/hooks/useFormSubmit';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getThemeManagementId, postImageItemsThemeManagemen, putThemeManagement } from '@/services/theme-management';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, message } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FormItem from './components/FormItem';

const START_DATE = dayjs().startOf('month').format('YYYY-MM-DD');
const END_DATE = dayjs().endOf('month').format('YYYY-MM-DD');

export default function UpdateTheme() {
	const [form] = Form.useForm();
	const { id } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const closePopupRef = useRef(null);

	const { data } = usePaginatedQuery(
		() =>
			getThemeManagementId({
				id: id,
			}),
		['themes-management-id', id]
	);

	useEffect(() => {
		if (data) {
			form.setFieldsValue({
				...data,
				startTime: data.startTime ? dayjs(data.startTime) : null,
				endTime: data.endTime ? dayjs(data.endTime) : null,
			});
		}
	}, [data, form]);

	const createThemeMutation = useMutation({
		mutationFn: (values) => {
			const payload = {
				themeName: values?.themeName || '',
				description: values?.description || "",
				startTime: values?.startTime,
				endTime: values?.endTime
			}
			return putThemeManagement({ id, params: payload });
		},
	});

	const handleOnSubmit = useFormSubmit(
		createThemeMutation,
		(values) => {
			return { ...values };
		},
		{
			onOk: () => {
				navigate(PATH.THEME_MANAGEMENT.ROOT, { replace: true });
			},
		}
	);

	const submitItemMutation = useMutation({
		mutationFn: (variables) => postImageItemsThemeManagemen(variables),
		onSuccess: () => {
			SuccessResponseModal({
				onOk: () => {
					queryClient.invalidateQueries({ queryKey: ['themes-management-id'] });
					closePopupRef.current?.();
				},
			});
		},
		onError: (error) => {
			const errorMessage = error?.data?.errorMessage || error?.response?.data?.errorMessage || 'Something went wrong';
			ErrorResponseModal({
				message: errorMessage,
			});
		},
	});

	const handleSubmitItem = async (payload, closePopup) => {
		const itemCode = payload?.itemCode;
		if (!itemCode) {
			message.error('Không xác định được loại item (itemCode)');
			return;
		}
		const images = payload?.newImages || (payload?.newImage ? [payload.newImage] : []);
		if (!images.length || !(images[0] instanceof File)) {
			message.error('Vui lòng chọn ảnh');
			return;
		}

		const formData = new FormData();
		// endpoint update item chỉ nhận 1 ảnh/lần -> lấy ảnh đầu tiên đã chọn
		formData.append('image', images[0]);
		if (payload.description !== undefined) {
			formData.append('description', payload.description || '');
		}

		closePopupRef.current = closePopup;
		submitItemMutation.mutate({ params: formData, id, itemCode });
	};

	return (
		<div>
			<AppPageTitle title="Update Theme" />
			<AppForm
				form={form}
				onSubmit={handleOnSubmit}
				targetPath={PATH.THEME_MANAGEMENT.ROOT}
				buttonProps={{
					loading: createThemeMutation.isPending,
					disabled: createThemeMutation.isPending,
				}}
				disabled={createThemeMutation.isPending}
			>
				<FormItem
					isCreate={false}
					form={form}
					END_DATE={END_DATE}
					START_DATE={START_DATE}
					dataDefault={data?.items}
					handleSubmitItem={handleSubmitItem}
				/>
			</AppForm>
		</div>
	)
}
